import {AnalyticsEvent} from 'analytics/Analytics';
import {ACTION_CATEGORY, ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';

enum StorableAnalyticsAction {
  CLICKED_INVENTORY_SHORTCUT = 'CLICKED_INVENTORY_SHORTCUT',
  CLICKED_INVENTORY_LINK = 'CLICKED_INVENTORY_LINK',
  OPENED_STORABLE = 'OPENED_STORABLE',
  SEARCHED_STORABLES = 'SEARCHED_STORABLES',
  VIEWED_PLATE_RECORD = 'VIEWED_PLATE_RECORD',
  // WRITE actions
  ARCHIVED_STORABLES = 'ARCHIVED_STORABLES',
  CREATED_STORABLES = 'CREATED_STORABLES',
  MOVED_STORABLES = 'MOVED_STORABLES',
  TRANSFERRED_SAMPLES = 'TRANSFERRED_SAMPLES',
  UPDATED_CICO_STATUS = 'UPDATED_CICO_STATUS',
  UPDATED_STORABLES = 'UPDATED_STORABLES',
  // Label printing
  LABEL_PRINTED = 'LABEL_PRINTED',
  INITIATED_LABEL_PRINT = 'INITIATED_LABEL_PRINT',
  // Plate annotation and fill
  CLICKED_CLEAR_CELLS = 'CLICKED_CLEAR_CELLS',
  CHANGED_CELL_SELECTION = 'CHANGED_CELL_SELECTION',
  SELECTED_ANNOTATION_LAYER = 'SELECTED_ANNOTATION_LAYER',
  EDITED_ANNOTATIONS = 'EDITED_ANNOTATIONS',
  ADDED_SOURCES = 'ADDED_SOURCES',
  REMOVED_SOURCES = 'REMOVED_SOURCES',
  CHANGED_TRANSFER_TABLE_VIEW_MODE = 'CHANGED_TRANSFER_TABLE_VIEW_MODE',
  EDITED_TRANSFER_TABLE = 'EDITED_TRANSFER_TABLE',
  TOGGLED_PLATE_DEFINITION_TYPE = 'TOGGLED_PLATE_DEFINITION_TYPE',
  SAVED_FIXED_PLATE_FROM_MODAL = 'SAVED_FIXED_PLATE_FROM_MODAL',
  ADDED_ANNOTATION_LATER = 'ADDED_ANNOTATION_LAYER',
  REMOVED_ANNOTATION_LATER = 'REMOVED_ANNOTATION_LATER',
}

// Keep in sync with benchling/analytics/lib/samples_analytics_event.py
export const STORABLE_ANALYTICS_ACTIONS: {[key in StorableAnalyticsAction]: AnalyticsEvent} = {
  // READ actions
  CLICKED_INVENTORY_SHORTCUT: {
    module: ANALYTICS_MODULE.SAMPLES,
    action: 'clicked_inventory_shortcut',
    actionCategory: ACTION_CATEGORY.READ,
  },
  CLICKED_INVENTORY_LINK: {
    module: ANALYTICS_MODULE.SAMPLES,
    action: 'clicked_inventory_link',
    actionCategory: ACTION_CATEGORY.READ,
  },
  OPENED_STORABLE: {
    module: ANALYTICS_MODULE.SAMPLES,
    action: 'opened_storable',
    actionCategory: ACTION_CATEGORY.READ,
  },
  SEARCHED_STORABLES: {
    module: ANALYTICS_MODULE.SAMPLES,
    action: 'searched_storables',
    actionCategory: ACTION_CATEGORY.READ,
  },
  VIEWED_PLATE_RECORD: {
    module: ANALYTICS_MODULE.SAMPLES,
    action: 'viewed_plate_transfer_record',
    actionCategory: ACTION_CATEGORY.READ,
  },
  // WRITE actions
  ARCHIVED_STORABLES: {
    module: ANALYTICS_MODULE.SAMPLES,
    action: 'archived_storables',
    actionCategory: ACTION_CATEGORY.WRITE,
  },
  CREATED_STORABLES: {
    module: ANALYTICS_MODULE.SAMPLES,
    action: 'created_storables',
    actionCategory: ACTION_CATEGORY.WRITE,
  },
  MOVED_STORABLES: {
    module: ANALYTICS_MODULE.SAMPLES,
    action: 'moved_storables',
    actionCategory: ACTION_CATEGORY.WRITE,
  },
  TRANSFERRED_SAMPLES: {
    module: ANALYTICS_MODULE.SAMPLES,
    action: 'transferred_samples',
    actionCategory: ACTION_CATEGORY.WRITE,
  },
  UPDATED_CICO_STATUS: {
    module: ANALYTICS_MODULE.SAMPLES,
    action: 'updated_cico_status',
    actionCategory: ACTION_CATEGORY.WRITE,
  },
  UPDATED_STORABLES: {
    module: ANALYTICS_MODULE.SAMPLES,
    action: 'updated_storables',
    actionCategory: ACTION_CATEGORY.WRITE,
  },
  // Label printing
  LABEL_PRINTED: {
    module: ANALYTICS_MODULE.SAMPLES,
    action: 'label_printed',
    actionCategory: ACTION_CATEGORY.READ,
  },
  INITIATED_LABEL_PRINT: {
    module: ANALYTICS_MODULE.SAMPLES,
    action: 'initiated_label_print',
    actionCategory: ACTION_CATEGORY.READ,
  },
  CLICKED_CLEAR_CELLS: {
    module: ANALYTICS_MODULE.SAMPLES,
    action: 'clicked_clear_cells',
    actionCategory: ACTION_CATEGORY.READ,
  },
  CHANGED_CELL_SELECTION: {
    module: ANALYTICS_MODULE.SAMPLES,
    action: 'changed_cell_selection',
    actionCategory: ACTION_CATEGORY.READ,
  },
  SELECTED_ANNOTATION_LAYER: {
    module: ANALYTICS_MODULE.SAMPLES,
    action: 'selected_annotation_layer',
    actionCategory: ACTION_CATEGORY.READ,
  },
  EDITED_ANNOTATIONS: {
    module: ANALYTICS_MODULE.SAMPLES,
    action: 'edited_annotations',
    actionCategory: ACTION_CATEGORY.READ,
  },
  SAVED_FIXED_PLATE_FROM_MODAL: {
    module: ANALYTICS_MODULE.SAMPLES,
    action: 'saved_fixed_plate_from_modal',
    actionCategory: ACTION_CATEGORY.WRITE,
  },
  ADDED_SOURCES: {
    module: ANALYTICS_MODULE.SAMPLES,
    action: 'added_sources',
    actionCategory: ACTION_CATEGORY.READ,
  },
  REMOVED_SOURCES: {
    module: ANALYTICS_MODULE.SAMPLES,
    action: 'removed_sources',
    actionCategory: ACTION_CATEGORY.READ,
  },
  CHANGED_TRANSFER_TABLE_VIEW_MODE: {
    module: ANALYTICS_MODULE.SAMPLES,
    action: 'changed_transfer_table_view_mode',
    actionCategory: ACTION_CATEGORY.READ,
  },
  EDITED_TRANSFER_TABLE: {
    module: ANALYTICS_MODULE.SAMPLES,
    action: 'edited_transfer_table',
    actionCategory: ACTION_CATEGORY.READ,
  },
  TOGGLED_PLATE_DEFINITION_TYPE: {
    module: ANALYTICS_MODULE.SAMPLES,
    action: 'toggled_plate_definition_type',
    actionCategory: ACTION_CATEGORY.READ,
  },
  ADDED_ANNOTATION_LAYER: {
    module: ANALYTICS_MODULE.SAMPLES,
    action: 'added_annotation_layer',
    actionCategory: ACTION_CATEGORY.READ,
  },
  REMOVED_ANNOTATION_LATER: {
    module: ANALYTICS_MODULE.SAMPLES,
    action: 'removed_annotation_layer',
    actionCategory: ACTION_CATEGORY.READ,
  },
};

export enum FixedPlateModalType {
  ANNOTATE_PLATE = 'annotate-plate',
  FILL_PLATE = 'fill-plate',
  FIXED_PLATE_EDITOR = 'fixed-plate-editor',
  PLATE_MAP_VIEW_DETAILS_MODAL = 'plate-transfer-record-detail-modal',
}

export enum FixedPlateModalStep {
  ANNOTATE_PLATE_STEP = 'annotate-plate',
  TRANSFER_SOURCES_STEP = 'transfer-sources',
  TRANSFER_QUANTITIES_STEP = 'transfer-quantities',
}
